/**
 * Auth-Kontext — vereint `AuthService` und `LoginService` des Altclients und übernimmt die
 * Aufgabe von `LoginDisplay.razor`, sich bei jeder Kontoänderung neu anzumelden.
 */

import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';

import { setCurrentAppUriProvider } from '@/api/requestContext';
import { msalConfig } from '@/config/authConfig';
import type { App, ChinaAppSysGroupView, ChinaAppSysView } from '@/types/entities';
import type { EntityTypes } from '@/types/enums';

import { AuthContext } from './authContext';
import { isDemoMode } from './demoAccount';
import type { AuthSession } from './authSession';
import { createAuthSession, loadApps as loadSessionApps, performLogin } from './authSession';
import { writeStoredCurrentApp } from './currentAppStorage';
import { getAuthenticatedAccount, msalInstance } from './msalInstance';
import { navigateToWithReload } from './navigation';
import { createViewPermissions, findViewIdByEntity, isAdministrator } from './permissions';

export type { AuthContextValue } from './authContext';
export type { App } from '@/types/entities';

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const { accounts } = useMsal();
  const [session] = useState<AuthSession>(createAuthSession);
  const [snapshot, setSnapshot] = useState<AuthSession>(() => ({ ...session }));
  const [initializing, setInitializing] = useState(true);
  const handledAccounts = useRef<string | null>(null);

  // Registrierung bereits während des Renderns: Effekte der Kinder laufen vor denen des
  // Providers, bilden aber schon App-bezogene Pfade. Die Closure liest den veränderlichen
  // Sitzungszustand und liefert deshalb immer den aktuellen Wert.
  setCurrentAppUriProvider(() => session.currentApp?.URI);

  /** Übernimmt den veränderlichen Sitzungszustand in den Renderzustand. */
  const publish = useCallback(() => {
    setSnapshot({ ...session });
  }, [session]);

  const login = useCallback(async (): Promise<boolean> => {
    session.authorized = await performLogin(session);
    publish();
    return session.authorized;
  }, [publish, session]);

  const logout = useCallback(async (): Promise<void> => {
    session.user = null;
    session.groups = null;
    publish();

    // Ohne Konto — etwa im logout-callback — würde `logoutRedirect` erneut zum
    // Abmelde-Endpunkt springen; `AuthService.Logout()` beendet dort ebenfalls nur den
    // lokalen Zustand. Im Demobetrieb gibt es keine Anmeldung, die zu beenden wäre.
    if (isDemoMode || getAuthenticatedAccount() === null) {
      return;
    }
    await msalInstance.logoutRedirect({
      postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
    });
  }, [publish, session]);

  const loadApps = useCallback(async (): Promise<App[]> => {
    const apps = await loadSessionApps(session);
    publish();
    return apps;
  }, [publish, session]);

  const setCurrentApp = useCallback(
    (app: App | null): void => {
      session.currentApp = app;
      publish();
    },
    [publish, session],
  );

  const setViews = useCallback(
    (views: ChinaAppSysView[]): void => {
      session.views = views;
      publish();
    },
    [publish, session],
  );

  const selectApp = useCallback(
    async (app: App): Promise<void> => {
      session.currentApp = app;
      writeStoredCurrentApp(app);
      session.user = null;
      session.groups = null;
      publish();

      await login();
      navigateToWithReload('/');
    },
    [login, publish, session],
  );

  const getViewIdByEntity = useCallback(
    (entity: EntityTypes): number | null => findViewIdByEntity(session.views, entity),
    [session],
  );

  const getViewPermissions = useCallback(
    (viewId: number): ChinaAppSysGroupView => createViewPermissions(session.groups, viewId),
    [session],
  );

  // Pendant zu `LoginDisplay.OnInitializedAsync` und `AuthenticationStateChanged`.
  const accountKey = accounts.map((account) => account.homeAccountId).join('|');
  useEffect(() => {
    if (handledAccounts.current === accountKey) {
      return;
    }
    handledAccounts.current = accountKey;

    const runLogin = async (): Promise<void> => {
      try {
        await login();
      } finally {
        setInitializing(false);
      }
    };
    void runLogin();
  }, [accountKey, login]);

  const value = useMemo(
    () => ({
      authorized: snapshot.authorized,
      initializing,
      apps: snapshot.apps,
      currentApp: snapshot.currentApp,
      user: snapshot.user,
      groups: snapshot.groups,
      views: snapshot.views,
      profileImage: snapshot.profileImage,
      company: snapshot.company,
      isAdministrator: isAdministrator(snapshot.groups),
      login,
      logout,
      loadApps,
      selectApp,
      setCurrentApp,
      setViews,
      getViewIdByEntity,
      getViewPermissions,
    }),
    [
      getViewIdByEntity,
      getViewPermissions,
      initializing,
      loadApps,
      login,
      logout,
      selectApp,
      setCurrentApp,
      setViews,
      snapshot,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
